import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, RotateCcw, Settings as SettingsIcon, Check, BarChart3 } from "lucide-react";
import SettingsModal from "./components/setting";
import Tasks from "./components/tasks";
import Statistics from "./components/Statistics";
import Account from "./components/account";

import { Mode } from "./ types";
import { formatTime } from "./utils";
import { useAppStore, useTimer, useSettings } from "./store";
import { useConfigStore } from "./useConfigStore";
import { useAuthStore } from "./authStore";

const App: React.FC = () => {
    const {  loadConfig } = useConfigStore();


  
  const modes = {
    pomodoro: { label: "Pomodoro", timeKey: "pomodoro" },
    shortBreak: { label: "Short Break", timeKey: "shortBreak" },
    longBreak: { label: "Long Break", timeKey: "longBreak" },
  };

  // Get state from store
  const { timeLeft, isRunning, progress, current_mode, completedPomodoros } = useTimer();
  const settings = useSettings();
  const { toggleTimer, resetTimer, switchMode,  tick, initialize } = useAppStore();
  const { initializeAuth } = useAuthStore();

  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showStatistics, setShowStatistics] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize store on mount
  useEffect(() => {
    const initializeApp = async () => {
      await loadConfig();
      await initialize();
      await initializeAuth();
    };
    initializeApp();
  }, [initialize, loadConfig, initializeAuth]);

  // Timer tick effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isRunning) {
      interval = setInterval(() => {
        tick();
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, tick]);

  // Update document title and favicon
  useEffect(() => {
    document.title = `${current_mode} : ${formatTime(timeLeft)}`;
    const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (favicon) {
      favicon.href = current_mode === "pomodoro" ? "/green.png" : "/red.png";
    }
  }, [timeLeft, current_mode]);

  // Get background color based on mode
  const getBackgroundColor = () => {
    switch (current_mode) {
      case "pomodoro":
        return settings.pomodoro.color;
      case "shortBreak":
        return settings.shortBreak.color;
      case "longBreak":
        return settings.longBreak.color;
      default:
        return settings.pomodoro.color;
    }
  };

  return (
    <div
      className="min-h-screen transition-colors duration-500"
      style={{ backgroundColor: getBackgroundColor() }}
    >
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-white text-2xl font-bold">Pomozid</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowStatistics(true)}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            >
              <BarChart3 size={24} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            >
              <SettingsIcon size={24} />
            </button>
            <Account />
          </div>
        </div>

        <div className="flex flex-col   gap-6 w-full justify-center  items-center  mx-auto">
          {/* Main Timer Card */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 w-full lg:w-1/3 mx-auto lg:mx-0">
            {/* Mode Tabs */}
            <div className="flex bg-black bg-opacity-20 rounded-lg p-1 mb-8">
              {Object.entries(modes).map(([mode, config]) => (
                <button
                  key={config.timeKey}
                  onClick={() => switchMode(mode as Mode)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    current_mode === config.timeKey
                      ? "bg-white bg-opacity-30 text-white"
                      : "text-white text-opacity-80 hover:text-opacity-100"
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>

            {/* Timer Display */}
            <div className="text-center mb-8">
              <div className="relative mb-6">
                <svg
                  className="w-64 h-64 mx-auto transform -rotate-90"
                  viewBox="0 0 100 100"
                >
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="white"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 45}`}
                    strokeDashoffset={`${
                      2 * Math.PI * 45 * (1 - progress / 100)
                    }`}
                    className="transition-all duration-1000 ease-linear"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-6xl font-bold text-white tabular-nums">
                    {formatTime(timeLeft)}
                  </span>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex gap-4 justify-center">
                <button
                  onClick={toggleTimer}
                  className="bg-white text-gray-800 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  {isRunning ? <Pause size={20} /> : <Play size={20} />}
                  {isRunning ? "PAUSE" : "START"}
                </button>
                <button
                  onClick={resetTimer}
                  className="bg-white bg-opacity-20 text-white hover:bg-opacity-30 px-4 py-4 rounded-lg transition-colors"
                >
                  <RotateCcw size={20} />
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="text-center">
              <div className="text-white text-opacity-80 text-sm mb-2">
                Pomodoros completed today
              </div>
              <div className="flex justify-center gap-2 flex-wrap">
                {Array.from(
                  { length: Math.max(8, completedPomodoros) },
                  (_, i) => (
                    <div
                      key={i}
                      className={`w-6 h-6 rounded border-2 border-white border-opacity-40 flex items-center justify-center ${
                        i < completedPomodoros ? "bg-white bg-opacity-80" : ""
                      }`}
                    >
                      {i < completedPomodoros && (
                        <Check size={14} className="text-gray-800" />
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Tasks Section */}
          <div className="flex  bg-white justify-center items-center bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 w-full lg:w-1/3 mx-auto lg:mx-0 ">
          
            <Tasks />
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal
    
          onClose={() => {
            setShowSettings(false);
          }}
        />
      )}

      {/* Statistics Modal */}
      {showStatistics && (
        <Statistics onClose={() => setShowStatistics(false)} />
      )}

      {/* Audio element for notifications */}
      <audio
        ref={audioRef}
        src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmkcBjuW2u6qbS4ELYnQ5eCNNwYSXbTq6KdWFApHoN3wv2scBTqQ1+/PfCwGKXnJ8d2QQAoWYLfr5atZEQg9mNzwwGocBzqS2O7ViiYEKoXE8tmJNgcYZrnt5qNOGwE4kdXwzncnBi17yuDckWAGEVy16OyrYB4BMabY8coUBQsplcDx0H4kBClr0+zbmF4IH2bA9sVtHgE9lr/xwHoPBCppzu7SiV4FE2a46eSvXhsGQ5/K8sZ6IQUqZtDq14VfBw5ZuOLkq2IZBjqi1+fOhEIEIXTJ8t6QQAoUX7Pr56tYEAg8l9rwwmkdBzuR2u/ViiYEK4XE8diJNgcZZrnt5qJOGwA4kdXwzncnBi17yuDcrmgBBiGH1v/HgykKLYjJ8NrDciUSXbbq5qJQFAQ9ltvm0YUjBSlmy+7WiF8FEWm46Oaube8AACAASURBVOUUBB03gNzvkUESXL/v4qRSEgQ9ltvm0YQjBSdmy+7WiF8FFGm46OSvXhoGOaLZ5tGFJAQsdcvq2IdYDwdXtuLkq2MWBTCS3u7IeikFJnrJ8OGRPwoWX7br56tYFAc8ltruwmokBSl+yu/BeCMEJHPH8d+QPAoXYLbp6KdXFAc8ltruwmokBSl+yu/BeCMEJHPH8d+QPAoXYLbq6KdXFAc8ltruwmokBSl+yu/BeCMEJHPH8d+QPAoXYLbr6KdXFAg8ltruwr4jASgcBSl+yu/BeCMEJHPH8d+QPAoXYLbr6KdX"
      />
    </div>
  );
};

export default App;